import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import LoadingScreen from '../components/LoadingScreen';
import { Star } from 'lucide-react';

interface AssessmentDetail {
  id: string;
  scores: {
    overall: number;
    categories: {
      inherent: {
        total: number;
        average: number;
        attributes: {
          spiritual: number;
          nutritional: number;
          physical: number;
          emotioPsychoRational: number;
          legendarial: number;
        };
      };
      coherent: {
        total: number;
        average: number;
        attributes: {
          religioTraditioCultural: number;
          financial: number;
          environmental: number;
          socioRelational: number;
          occupational: number;
        };
      };
    };
    individualAttributes: {
      spiritual: number;
      nutritional: number;
      physical: number;
      emotioPsychoRational: number;
      legendarial: number;
      religioTraditioCultural: number;
      financial: number;
      environmental: number;
      socioRelational: number;
      occupational: number;
    };
  };
  results: {
    overallLevel: string;
    categoryLevels: {
      inherent: string;
      coherent: string;
    };
    insights: {
      strengths: string[];
      areasForImprovement: string[];
      recommendations: string[];
    };
    radarData: Array<{
      attribute: string;
      score: number;
      category: string;
    }>;
  };
  createdAt: string;
}

interface QuestionData {
  id: string;
  attribute: string;
  category: string;
  question: string;
  tips: string;
}

const StarOfWellBeingDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  // const { user } = useAuth(); // Commented out as not used
  const [assessmentData, setAssessmentData] = useState<AssessmentDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Assessment questions based on the Star of Well-Being framework
  const questions: QuestionData[] = [
    {
      id: 'spiritual',
      attribute: 'Spiritual',
      category: 'Inherent',
      question: 'How connected do you feel to your values, purpose, or spiritual beliefs?',
      tips: 'Consider whether you feel guided by a deeper sense of meaning, inner peace, or moral compass in daily life.'
    },
    {
      id: 'nutritional',
      attribute: 'Nutritional',
      category: 'Inherent',
      question: 'How well do your eating habits support your health and energy needs?',
      tips: 'Think about food quality, balance, hydration, and whether you eat in a way that sustains your well-being.'
    },
    {
      id: 'physical',
      attribute: 'Physical',
      category: 'Inherent',
      question: 'How would you describe your current physical health?',
      tips: 'Include energy levels, fitness, mobility, and absence or presence of illness or pain.'
    },
    {
      id: 'emotioPsychoRational',
      attribute: 'Emotio-Psycho-Rational',
      category: 'Inherent',
      question: 'How well do you manage emotions, thoughts, and decision-making?',
      tips: 'Consider emotional stability, stress coping, self-talk, and clarity in judgment.'
    },
    {
      id: 'legendarial',
      attribute: 'Legendarial',
      category: 'Inherent',
      question: 'How clear and fulfilled do you feel in your life\'s purpose and legacy?',
      tips: 'Reflect on whether your actions align with your long-term goals and how you contribute beyond yourself.'
    },
    {
      id: 'religioTraditioCultural',
      attribute: 'Religio-Traditio-Cultural',
      category: 'Coherent',
      question: 'How well do your traditions, beliefs, and cultural identity support your well-being?',
      tips: 'Consider harmony between your personal values and the traditions or culture you engage with.'
    },
    {
      id: 'financial',
      attribute: 'Financial',
      category: 'Coherent',
      question: 'How secure and confident do you feel about your financial situation?',
      tips: 'Include income stability, ability to meet needs, savings, and financial planning.'
    },
    {
      id: 'environmental',
      attribute: 'Environmental',
      category: 'Coherent',
      question: 'How supportive is your physical environment for your health and well-being?',
      tips: 'Think about home, workplace, access to nature, and safety in your surroundings.'
    },
    {
      id: 'socioRelational',
      attribute: 'Socio-Relational',
      category: 'Coherent',
      question: 'How strong and supportive are your personal and professional relationships?',
      tips: 'Consider trust, communication, and sense of belonging in your relationships.'
    },
    {
      id: 'occupational',
      attribute: 'Occupational',
      category: 'Coherent',
      question: 'How satisfied and engaged are you in your current work or daily role?',
      tips: 'Reflect on fulfillment, work-life balance, and whether your role aligns with your skills and passions.'
    }
  ];

  useEffect(() => {
    const fetchAssessmentDetails = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await fetch(API_CONFIG.ENDPOINTS.STAR_OF_WELLBEING_LATEST, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          
          // Validate data structure
          if (data.data && data.data.scores) {
            // Compute overall score from category averages if not provided
            const computedData = { ...data.data };
            
            if (!computedData.scores.overall) {
              const inherentAvg = computedData.scores.categories?.inherent?.average || 0;
              const coherentAvg = computedData.scores.categories?.coherent?.average || 0;
              computedData.scores.overall = (inherentAvg + coherentAvg) / 2 * 10; // Convert to 0-100 scale
            }
            
            setAssessmentData(computedData);
          } else {

            navigate('/panel/wellness/');
          }
        } else if (response.status === 404) {

          navigate('/panel/learn/');
        } else {

          navigate('/panel/wellness/');
        }
      } catch (error) {

        navigate('/panel/wellness/');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssessmentDetails();
  }, [navigate]);

  const getStarLevelInfo = (score: number) => {
    switch (Math.round(score)) {
      case 1:
        return {
          level: 'Needs Assistance',
          color: '#E74C3C', // Soft Red or Coral
          meaning: 'Low well-being / imbalance',
          message: 'Signals attention, urgency, or support needed'
        };
      case 2:
        return {
          level: 'Developing Awareness',
          color: '#F39C12', // Amber or Warm Orange
          meaning: 'Beginning improvement',
          message: 'Encourages motivation and growth'
        };
      case 3:
        return {
          level: 'Stable & Improving',
          color: '#1ABC9C', // Teal-Green
          meaning: 'Balanced and consistent state',
          message: 'Reflects healing, balance, and progress'
        };
      case 4:
        return {
          level: 'Ideal State',
          color: '#F1C40F', // 
          meaning: 'Optimal well-being / thriving',
          message: 'Represents enlightenment, vitality, and excellence'
        };
      default:
        return {
          level: 'Unknown',
          color: '#6B7280',
          meaning: 'Unknown',
          message: 'Unknown'
        };
    }
  };


  if (isLoading) {
    return <LoadingScreen message="Loading assessment details..." fullScreen />;
  }

  if (!assessmentData) {
    return (
      <div className="star-details-page">
        <div className="details-container">
          <div className="no-data-message">
            <h2>No Assessment Data Found</h2>
            <p>Please complete the Star of Well-Being assessment first.</p>
          </div>
        </div>
      </div>
    );
  }

  const CircularProgressBar: React.FC<{ percentage: number, color: string, label: string, status: string }> = ({ percentage, color, label, status }) => {
    const progress = percentage / 100;

    return (
      <div className="circular-progress-container">
        <div
          className="circular-progress"
          style={{
            '--progress': progress,
            '--color': color
          } as React.CSSProperties}
        >
          <div className="circular-progress-content">
            <span className="circular-progress-percentage">{percentage}%</span>
          </div>
        </div>
        <div className="circular-progress-text">
          <p className="circular-progress-label">{label}</p>
          <p className="circular-progress-status">{status}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="star-details-page">
      {/* Header with Navigation */}
      <div className="details-header">
        <button className="back-btn" onClick={() => navigate('/panel/wellness/')}>
          <span className="material-symbols-outlined">arrow_back</span> Back to Wellness
        </button>
        <div className="header-top">
          <div className="header-content">
            <h1 className="page-title">SOWB Assessment Results</h1>
            <p className="page-subtitle">Your comprehensive Star of Well-Being overview</p>
          </div>
        </div>

        {/* Overall Score Section */}
        <div className="overall-score-section">
          <div className="score-card">
            <div className="score-label">Overall Well-Being Score</div>
            <div className="score-display">
              <span className="score-number">{((assessmentData.scores.overall / 10) / 4 * 100).toFixed(0)}</span>
              <span className="score-max">%</span>
            </div>
            <div className="score-level" style={{ color: getStarLevelInfo(assessmentData.scores.overall / 10).color }}>
              {getStarLevelInfo(assessmentData.scores.overall / 10).level}
            </div>
          </div>

          {/* Category Summary */}
          <div className="category-summary">
            <div className="category-card inherent">
              <h3>Inherent Attributes</h3>
              <div className="category-score">{assessmentData.scores.categories.inherent.average.toFixed(1)}</div>
              <p>Personal Foundations</p>
            </div>
            <div className="category-card coherent">
              <h3>Coherent Attributes</h3>
              <div className="category-score">{assessmentData.scores.categories.coherent.average.toFixed(1)}</div>
              <p>External Harmony</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="details-content">
        {/* Status Cards Grid */}
        <div className="section-header">
          <h2>Attribute Breakdown</h2>
          <p>Your scores across the 10 well-being dimensions</p>
        </div>
        <div className="status-card-grid">
          {questions.map((question) => {
            const score = assessmentData.scores.individualAttributes[question.id as keyof typeof assessmentData.scores.individualAttributes];
            const levelInfo = getStarLevelInfo(score);
            const isHighlighted = score === 1; // Highlight level 1 (needs assistance)

            return (
              <div
                key={question.id}
                className={`status-card ${isHighlighted ? 'highlighted' : ''}`}
                style={{
                  borderColor: levelInfo.color,
                  '--card-color': levelInfo.color
                } as React.CSSProperties}
              >
                <div
                  className="status-card-header"
                  style={{ backgroundColor: levelInfo.color }}
                />
                <div className="status-card-content">
                  <p className="status-attribute">{question.attribute}</p>
                  <div className="status-stars" style={{ color: levelInfo.color }}>
                    {[1, 2, 3, 4].map((star) => (
                      <Star
                        key={star}
                        size={20}
                        fill={star <= score ? 'currentColor' : 'none'}
                        stroke={star <= score ? 'currentColor' : '#d1d5db'}
                        className="status-star"
                      />
                    ))}
                  </div>
                  <p className="status-level">{levelInfo.level}</p>
                  <p className="status-description">{levelInfo.meaning}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StarOfWellBeingDetailsPage;


